import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Boxes } from "lucide-react";

export default function Auth() {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", company_name: "" });
  const { setAuth } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (mode) => async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : form;
      const { data } = await api.post(`/auth/${mode}`, payload);
      setAuth(data);
      navigate(data.membership ? "/dashboard" : "/onboarding");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const googleBtn = (
    <Button type="button" variant="outline" className="w-full" onClick={googleSignIn} data-testid="google-signin-btn">
      <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
      Continue with Google
    </Button>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid="auth-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 font-heading font-extrabold text-xl mb-6">
          <Boxes className="h-6 w-6 text-primary" /> StockPilot PH
        </Link>
        <div className="border rounded-xl bg-card p-6 sm:p-8 fade-in">
          <Tabs defaultValue="login">
            <TabsList className="grid grid-cols-2 w-full mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Sign in</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Create account</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              <form onSubmit={submit("login")} className="space-y-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" data-testid="login-email-input" value={form.email} onChange={set("email")} required />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input type="password" data-testid="login-password-input" value={form.password} onChange={set("password")} required />
                </div>
                {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}
                <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                  {loading ? "Signing in…" : "Sign in"}
                </Button>
                {googleBtn}
                <p className="text-xs text-muted-foreground text-center pt-2">
                  Demo: owner@demo.ph / DemoOwner123!
                </p>
              </form>
            </TabsContent>
            <TabsContent value="register">
              <form onSubmit={submit("register")} className="space-y-4">
                <div className="space-y-2">
                  <Label>Full name</Label>
                  <Input data-testid="register-name-input" value={form.name} onChange={set("name")} required />
                </div>
                <div className="space-y-2">
                  <Label>Company name</Label>
                  <Input data-testid="register-company-input" value={form.company_name} onChange={set("company_name")} placeholder="Your restaurant group" />
                  <p className="text-xs text-muted-foreground">Leave blank if you were invited by email — you'll join that company.</p>
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" data-testid="register-email-input" value={form.email} onChange={set("email")} required />
                </div>
                <div className="space-y-2">
                  <Label>Password (min 8 chars)</Label>
                  <Input type="password" data-testid="register-password-input" value={form.password} onChange={set("password")} required minLength={8} />
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-btn">
                  {loading ? "Creating…" : "Create account"}
                </Button>
                {googleBtn}
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
